<template>
  <div class="affordability-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.compliance.affordability.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.compliance.affordability.subtitle') }}</p>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="filter-bar">
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.compliance.affordability.filter_status') }}</label>
        <select v-model="filters.status" @change="resetAndFetch">
          <option value="">{{ $t('admin.compliance.affordability.status_all') }}</option>
          <option value="triggered">Triggered</option>
          <option value="pending_review">{{ $t('admin.compliance.affordability.status_pending') }}</option>
          <option value="enhanced_interaction">Enhanced Interaction</option>
          <option value="passed">{{ $t('admin.compliance.affordability.status_approved') }}</option>
          <option value="failed">{{ $t('admin.compliance.affordability.status_rejected') }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.compliance.affordability.filter_trigger') }}</label>
        <select v-model="filters.trigger_type" @change="resetAndFetch">
          <option value="">{{ $t('admin.compliance.affordability.trigger_all') }}</option>
          <option value="deposit_threshold">{{ $t('admin.compliance.affordability.trigger_deposit_threshold') }}</option>
          <option value="loss_threshold">{{ $t('admin.compliance.affordability.trigger_loss_threshold') }}</option>
          <option value="time_threshold">Time Threshold</option>
        </select>
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.compliance.affordability.filter_from') }}</label>
        <input v-model="filters.date_from" type="date" class="filter-input" @change="resetAndFetch" />
      </div>
      <div class="filter-group">
        <label class="filter-label">{{ $t('admin.compliance.affordability.filter_to') }}</label>
        <input v-model="filters.date_to" type="date" class="filter-input" @change="resetAndFetch" />
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <span class="spinner"></span>
      {{ $t('admin.compliance.affordability.loading') }}
    </div>

    <!-- Assessments Table -->
    <div v-else class="table-container">
      <table>
        <thead>
          <tr>
            <th class="col-id">{{ $t('admin.compliance.affordability.col_id') }}</th>
            <th class="col-player">{{ $t('admin.compliance.affordability.col_player') }}</th>
            <th class="col-trigger">{{ $t('admin.compliance.affordability.col_trigger') }}</th>
            <th class="col-amount">{{ $t('admin.compliance.affordability.col_amount') }}</th>
            <th class="col-status">{{ $t('admin.compliance.affordability.col_status') }}</th>
            <th class="col-date">{{ $t('admin.compliance.affordability.col_created') }}</th>
            <th class="col-actions">{{ $t('admin.compliance.affordability.col_actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="assessments.length === 0">
            <td colspan="7" class="empty-row">{{ $t('admin.compliance.affordability.empty') }}</td>
          </tr>
          <tr
            v-for="item in assessments"
            :key="item.id"
            class="assessment-row"
            @click="openReviewModal(item)"
          >
            <td class="mono-value">#{{ item.id }}</td>
            <td>
              <div class="player-info">
                <div class="player-avatar">{{ getInitials(item.username ?? '') }}</div>
                <div>
                  <div class="player-name">{{ item.username }}</div>
                  <div class="player-sub">{{ item.email }}</div>
                </div>
              </div>
            </td>
            <td>
              <span class="rule-tag">{{ formatTriggerType(item.trigger_type) }}</span>
            </td>
            <td class="mono-value">
              <span dir="ltr">{{ formatCents(item.trigger_amount) }}</span>
            </td>
            <td>
              <span class="badge" :class="statusBadgeClass(item.assessment_status)">
                {{ formatStatus(item.assessment_status) }}
              </span>
            </td>
            <td>{{ formatDate(item.created_at ?? '') }}</td>
            <td>
              <div class="action-buttons" @click.stop>
                <button class="btn-icon" title="Review Assessment" @click="openReviewModal(item)">
                  <AppIcon name="action-view" :size="14" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="totalPages > 0" class="pagination">
        <button
          class="pagination-btn"
          :disabled="currentPage <= 1"
          @click="goToPage(currentPage - 1)"
        >&larr;</button>
        <button
          v-for="page in visiblePages"
          :key="page"
          class="pagination-btn"
          :class="{ active: page === currentPage }"
          @click="goToPage(page)"
        >{{ page }}</button>
        <span class="pagination-info">
          {{ $t('admin.compliance.affordability.showing', { start: rangeStart, end: rangeEnd, total: totalItems }) }}
        </span>
        <button
          class="pagination-btn"
          :disabled="currentPage >= totalPages"
          @click="goToPage(currentPage + 1)"
        >&rarr;</button>
      </div>
    </div>

    <!-- Review Modal -->
    <div v-if="reviewModal.visible" class="modal-overlay" @click.self="closeReviewModal">
      <div class="modal-content modal-wide">
        <div class="modal-header">
          <h3 class="modal-title">{{ $t('admin.compliance.affordability.modal_title', { id: reviewModal.item?.id }) }}</h3>
          <button class="modal-close" @click="closeReviewModal">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Loading detail -->
          <div v-if="detailLoading" class="loading-state">
            <span class="spinner"></span>
            Loading assessment detail...
          </div>

          <template v-else-if="detail">
            <!-- Assessment Info -->
            <div class="detail-grid">
              <div class="detail-item">
                <div class="detail-label">Player</div>
                <div class="detail-value">
                  <router-link
                    :to="`/admin/players/${detail.player?.id}`"
                    class="player-link"
                    @click.stop
                  >{{ detail.player?.username }}</router-link>
                  <span class="player-email-sub">{{ detail.player?.email }}</span>
                </div>
              </div>
              <div class="detail-item">
                <div class="detail-label">VIP Level</div>
                <div class="detail-value">{{ formatVipLevel(detail.player?.vip_level ?? 0) }}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">Account Age</div>
                <div class="detail-value">{{ detail.player?.account_age_days }} days</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">Country</div>
                <div class="detail-value">{{ detail.player?.country || '—' }}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">Current Balance</div>
                <div class="detail-value mono-value" dir="ltr">${{ fmtMoney(detail.player?.balance ?? 0) }}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">Total Deposited</div>
                <div class="detail-value mono-value" dir="ltr">${{ fmtMoney(detail.player?.total_deposited ?? 0) }}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">Total Wagered</div>
                <div class="detail-value mono-value" dir="ltr">${{ fmtMoney(detail.player?.total_wagered ?? 0) }}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">Net Loss (30d)</div>
                <div class="detail-value mono-value loss-value" dir="ltr">${{ fmtMoney(detail.net_loss_30d) }}</div>
              </div>
            </div>

            <!-- Assessment Details -->
            <div class="detail-section">
              <div class="detail-section-title">Assessment Details</div>
              <div class="info-grid">
                <div class="info-row">
                  <span class="info-key">Trigger Type</span>
                  <span class="info-val">{{ formatTriggerType(detail.assessment.trigger_type) }}</span>
                </div>
                <div class="info-row">
                  <span class="info-key">Trigger Amount</span>
                  <span class="info-val mono-value" dir="ltr">{{ formatCents(detail.assessment.trigger_amount) }}</span>
                </div>
                <div class="info-row">
                  <span class="info-key">Current Status</span>
                  <span class="info-val">
                    <span class="badge" :class="statusBadgeClass(detail.assessment.assessment_status)">
                      {{ formatStatus(detail.assessment.assessment_status) }}
                    </span>
                  </span>
                </div>
                <div class="info-row">
                  <span class="info-key">Triggered At</span>
                  <span class="info-val">{{ formatDateTime(detail.assessment.created_at ?? '') }}</span>
                </div>
                <div v-if="detail.assessment.reviewed_at" class="info-row">
                  <span class="info-key">Reviewed At</span>
                  <span class="info-val">{{ formatDateTime(detail.assessment.reviewed_at ?? '') }}</span>
                </div>
                <div v-if="detail.assessment.reviewer" class="info-row">
                  <span class="info-key">Reviewed By</span>
                  <span class="info-val">{{ detail.assessment.reviewer?.name }}</span>
                </div>
              </div>
            </div>

            <!-- Previous Admin Notes -->
            <div v-if="detail.assessment.admin_notes" class="detail-section">
              <div class="detail-section-title">Previous Admin Notes</div>
              <div class="notes-box">{{ detail.assessment.admin_notes }}</div>
            </div>

            <!-- Recent Deposits -->
            <div v-if="detail.recent_deposits_30d.length > 0" class="detail-section">
              <div class="detail-section-title">Recent Deposits (Last 30 Days)</div>
              <div class="deposits-list">
                <div
                  v-for="dep in detail.recent_deposits_30d"
                  :key="dep.id"
                  class="deposit-row"
                >
                  <span class="deposit-amount mono-value" dir="ltr">${{ fmtMoney(dep.amount) }}</span>
                  <span class="deposit-date">{{ formatDateTime(dep.created_at) }}</span>
                </div>
              </div>
            </div>

            <!-- Review Form -->
            <div class="detail-section">
              <div class="detail-section-title">{{ $t('admin.compliance.affordability.section_action') }}</div>
              <div class="form-group">
                <label class="form-label">{{ $t('admin.compliance.affordability.form_status') }} <span class="required">*</span></label>
                <select v-model="reviewForm.status" class="form-select">
                  <option value="">Select status...</option>
                  <option value="pending_review">Pending Review</option>
                  <option value="enhanced_interaction">Enhanced Interaction</option>
                  <option value="passed">Passed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('admin.compliance.affordability.form_notes') }}</label>
                <textarea
                  v-model="reviewForm.notes"
                  class="form-textarea"
                  placeholder="Add notes about your review..."
                  rows="3"
                  maxlength="2000"
                ></textarea>
                <div class="char-count">{{ reviewForm.notes.length }}/2000</div>
              </div>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" :disabled="reviewForm.processing" @click="closeReviewModal">
            {{ $t('admin.compliance.affordability.btn_close') }}
          </button>
          <button
            class="btn btn-primary"
            :disabled="!reviewForm.status || reviewForm.processing || detailLoading"
            @click="submitReview"
          >
            <span v-if="reviewForm.processing" class="spinner spinner-sm"></span>
            {{ reviewForm.processing ? $t('admin.compliance.affordability.btn_submitting') : $t('admin.compliance.affordability.btn_submit') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()
const toast = useToastStore()

// ----------- Types -----------

interface AssessmentListItem {
  id: number
  user_id: number
  username: string | null
  email: string | null
  vip_level: number | null
  trigger_type: string
  trigger_amount: number | null
  assessment_status: string
  admin_user_id: number | null
  reviewed_at: string | null
  created_at: string | null
}

interface AssessmentDetail {
  assessment: {
    id: number
    user_id: number
    trigger_type: string
    trigger_amount: number | null
    assessment_status: string
    admin_user_id: number | null
    admin_notes: string | null
    reviewed_at: string | null
    player_response: string | null
    created_at: string | null
    reviewer: { id: number; name: string } | null
  }
  player: {
    id: number
    username: string
    email: string
    vip_level: number
    country: string | null
    account_age_days: number
    balance: number
    total_deposited: number
    total_wagered: number
  } | null
  recent_deposits_30d: { id: number; amount: number; created_at: string }[]
  net_loss_30d: number
}

interface PaginatedResponse {
  data: AssessmentListItem[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

// ----------- State -----------

const assessments = ref<AssessmentListItem[]>([])
const loading = ref(true)
const currentPage = ref(1)
const totalPages = ref(0)
const totalItems = ref(0)

const filters = ref({
  status: '',
  trigger_type: '',
  date_from: '',
  date_to: '',
})

// Review modal
const reviewModal = reactive({
  visible: false,
  item: null as AssessmentListItem | null,
})

const detail = ref<AssessmentDetail | null>(null)
const detailLoading = ref(false)

const reviewForm = reactive({
  status: '',
  notes: '',
  processing: false,
})

// ----------- Computed -----------

const rangeStart = computed(() => {
  if (totalItems.value === 0) return 0
  return (currentPage.value - 1) * 25 + 1
})

const rangeEnd = computed(() => {
  return Math.min(currentPage.value * 25, totalItems.value)
})

const visiblePages = computed(() => {
  const pages: number[] = []
  const total = totalPages.value
  const current = currentPage.value
  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)
  if (end - start < 4) {
    if (start === 1) end = Math.min(total, start + 4)
    else start = Math.max(1, end - 4)
  }
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
})

// ----------- Data Fetching -----------

function resetAndFetch() {
  currentPage.value = 1
  fetchAssessments()
}

async function fetchAssessments(): Promise<void> {
  loading.value = true
  try {
    const params: Record<string, string | number> = { page: currentPage.value }
    if (filters.value.status) params.status = filters.value.status
    if (filters.value.trigger_type) params.trigger_type = filters.value.trigger_type
    if (filters.value.date_from) params.date_from = filters.value.date_from
    if (filters.value.date_to) params.date_to = filters.value.date_to

    const response = await apiClient.get<PaginatedResponse>('/admin/api/affordability-assessments', { params })
    const data = response.data
    assessments.value = data.data
    currentPage.value = data.current_page
    totalPages.value = data.last_page
    totalItems.value = data.total
  } catch {
    toast.error(t('admin.compliance.affordability.error_load'))
  } finally {
    loading.value = false
  }
}

async function fetchDetail(id: number): Promise<void> {
  detailLoading.value = true
  detail.value = null
  try {
    const response = await apiClient.get<AssessmentDetail>(`/admin/api/affordability-assessments/${id}`)
    detail.value = response.data
  } catch {
    toast.error('Failed to load assessment detail')
  } finally {
    detailLoading.value = false
  }
}

// ----------- Review -----------

async function submitReview(): Promise<void> {
  if (!reviewModal.item || !reviewForm.status) return
  reviewForm.processing = true
  try {
    await apiClient.post(`/admin/api/affordability-assessments/${reviewModal.item.id}/review`, {
      status: reviewForm.status,
      admin_notes: reviewForm.notes || null,
    })
    toast.success(t('admin.compliance.affordability.success_review'))
    closeReviewModal()
    fetchAssessments()
  } catch {
    toast.error(t('admin.compliance.affordability.error_review'))
  } finally {
    reviewForm.processing = false
  }
}

// ----------- Modal -----------

function openReviewModal(item: AssessmentListItem) {
  reviewModal.item = item
  reviewModal.visible = true
  reviewForm.status = ''
  reviewForm.notes = ''
  reviewForm.processing = false
  fetchDetail(item.id)
}

function closeReviewModal() {
  if (reviewForm.processing) return
  reviewModal.visible = false
  reviewModal.item = null
  detail.value = null
}

// ----------- Pagination -----------

function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  fetchAssessments()
}

// ----------- Formatting -----------

function formatDate(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatDateTime(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function getInitials(username: string): string {
  if (!username) return '?'
  return username
    .split(/[_.\-\s]/)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function formatTriggerType(type: string): string {
  const map: Record<string, string> = {
    deposit_threshold: t('admin.compliance.affordability.trigger_deposit_threshold'),
    loss_threshold: t('admin.compliance.affordability.trigger_loss_threshold'),
    time_threshold: 'Time Threshold',
  }
  return map[type] ?? type
}

function formatStatus(status: string): string {
  const map: Record<string, string> = {
    triggered: 'Triggered',
    pending_review: t('admin.compliance.affordability.status_pending'),
    enhanced_interaction: 'Enhanced',
    passed: t('admin.compliance.affordability.status_approved'),
    failed: t('admin.compliance.affordability.status_rejected'),
  }
  return map[status] ?? status
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'triggered': return 'badge-triggered'
    case 'pending_review': return 'badge-pending'
    case 'enhanced_interaction': return 'badge-enhanced'
    case 'passed': return 'badge-passed'
    case 'failed': return 'badge-failed'
    default: return 'badge-triggered'
  }
}

function formatCents(cents: number | null): string {
  if (cents === null || cents === undefined) return '—'
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtMoney(amount: number): string {
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const VIP_LABELS: Record<number, string> = {
  0: 'Bronze',
  1: 'Silver',
  2: 'Gold',
  3: 'Platinum',
  4: 'Diamond',
}

function formatVipLevel(level: number): string {
  return VIP_LABELS[level] ?? `Level ${level}`
}

// ----------- Lifecycle -----------

onMounted(() => {
  fetchAssessments()
})
</script>

<style scoped>
.affordability-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

/* Filter Bar */
.filter-bar {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  white-space: nowrap;
}

.filter-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  transition: border-color 150ms ease;
  color-scheme: dark;
}

.filter-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.filter-bar select {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 150ms ease;
}

.filter-bar select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.filter-bar select option {
  background: var(--bg-secondary, #1a2c38);
  color: var(--text-primary, #fff);
}

/* Loading */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 64px 20px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-sm {
  width: 14px;
  height: 14px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

.table-container:hover {
  border-color: rgba(255, 255, 255, 0.1);
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-tertiary, #213743);
}

th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

.assessment-row {
  background: var(--bg-secondary, #1a2c38);
  transition: background 150ms ease;
  cursor: pointer;
}

.assessment-row:hover {
  background: #253d4f;
}

tbody tr:last-child td {
  border-bottom: none;
}

.empty-row {
  text-align: center;
  color: var(--text-tertiary, #7f8fa4);
  padding: 48px 16px !important;
}

.mono-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-primary, #fff);
}

/* Player Cell */
.player-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.player-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1475e1, #9b59b6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.player-name {
  font-size: 13px;
  color: var(--text-primary, #fff);
  font-weight: 600;
}

.player-sub {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Rule Tag */
.rule-tag {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  background: var(--bg-tertiary, #213743);
  padding: 4px 10px;
  border-radius: 9999px;
  white-space: nowrap;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.badge-triggered {
  background: rgba(237, 66, 69, 0.2);
  color: #ed4245;
}

.badge-pending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-enhanced {
  background: rgba(20, 117, 225, 0.15);
  color: var(--accent-secondary, #1475e1);
}

.badge-passed {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-failed {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

/* Actions */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  font-size: 14px;
  color: var(--text-tertiary, #7f8fa4);
}

.btn-icon:hover {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary, #00e701);
  border-color: rgba(0, 231, 1, 0.3);
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled) {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.active {
  background: var(--accent-primary, #00e701);
  color: #000;
  border-color: var(--accent-primary, #00e701);
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 12px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  background: #00cc01;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.15s ease;
}

.modal-content {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg, 12px);
  width: 100%;
  max-width: 560px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
  max-height: 90vh;
  overflow-y: auto;
}

.modal-wide {
  max-width: 680px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
  font-family: inherit;
}

.modal-close:hover {
  color: var(--text-primary, #fff);
}

.modal-body {
  padding: 20px 24px;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* Detail Grid */
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
}

.detail-value {
  font-size: 14px;
  color: var(--text-primary, #fff);
}

.loss-value {
  color: var(--color-danger, #ed4245);
}

.player-link {
  color: var(--accent-secondary, #1475e1);
  text-decoration: none;
  font-weight: 600;
}

.player-link:hover {
  text-decoration: underline;
}

.player-email-sub {
  display: block;
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 2px;
}

/* Detail Sections */
.detail-section {
  margin-bottom: 20px;
}

.detail-section-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

/* Info Grid (key-value rows) */
.info-grid {
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  padding: 4px 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.info-row:last-child {
  border-bottom: none;
}

.info-key {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

.info-val {
  font-size: 13px;
  color: var(--text-primary, #fff);
  font-weight: 600;
}

/* Notes Box */
.notes-box {
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  padding: 12px 16px;
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.5;
}

/* Deposits List */
.deposits-list {
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  padding: 4px 16px;
}

.deposit-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.deposit-row:last-child {
  border-bottom: none;
}

.deposit-amount {
  font-size: 13px;
  color: var(--color-success, #00e701);
  font-weight: 600;
}

.deposit-date {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Form */
.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 6px;
}

.required {
  color: var(--color-danger, #ed4245);
}

.form-select {
  width: 100%;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 150ms ease;
}

.form-select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-select option {
  background: var(--bg-secondary, #1a2c38);
  color: var(--text-primary, #fff);
}

.form-textarea {
  width: 100%;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 150ms ease;
  box-sizing: border-box;
}

.form-textarea:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-textarea::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.char-count {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  text-align: right;
  margin-top: 4px;
}

/* Column widths */
.col-id { width: 6%; }
.col-player { width: 24%; }
.col-trigger { width: 18%; }
.col-amount { width: 14%; }
.col-status { width: 14%; }
.col-date { width: 14%; }
.col-actions { width: 10%; }

/* Responsive */
@media (max-width: 1200px) {
  .filter-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  table {
    font-size: 12px;
  }

  td,
  th {
    padding: 10px 12px;
  }
}

@media (max-width: 768px) {
  .affordability-page {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }
}

/* RTL overrides */
[dir='rtl'] th,
[dir='rtl'] td {
  text-align: right;
}

[dir='rtl'] .player-info {
  flex-direction: row-reverse;
}

[dir='rtl'] .info-row,
[dir='rtl'] .deposit-row {
  flex-direction: row-reverse;
}

[dir='rtl'] .modal-footer {
  flex-direction: row-reverse;
}

[dir='rtl'] .pagination-info {
  direction: ltr;
}
</style>
